/**
 * Layout component using Bitcoin.com React CL WebLayout
 * Reference: https://github.com/Bert-Bitcoin/Bitcoin.com-React-CL
 */

import { ReactNode, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useConnectModal, useAccountModal } from '@rainbow-me/rainbowkit'
import { useAccount, useChainId, useSwitchChain } from 'wagmi'
import { mainnet } from 'wagmi/chains'
import { WebLayout } from '@bitcoin-com/react-components/src/layouts/weblayout'
import type { HeaderMenuItem } from '@bitcoin-com/react-components/src/components/header'
import type { WebsiteFooterLinkGroup } from '@bitcoin-com/react-components/src/components/website-footer'
import { formatAddress } from '../../utils/format'
import { toast } from 'react-hot-toast'
import { NetworkWarning } from '../common/NetworkWarning'
import { ThemeToggle } from '../common/ThemeToggle'
import { setSentryUser, clearSentryUser, addBreadcrumb } from '../../config/sentry'

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { openConnectModal } = useConnectModal()
  const { openAccountModal } = useAccountModal()
  const { address, isConnected, chain } = useAccount()
  const chainId = useChainId()
  const { switchChain: _switchChain } = useSwitchChain()
  
  // Check if user is on Ethereum mainnet
  const isWrongNetwork = isConnected && chainId !== mainnet.id
  
  // Track wallet connection in Sentry
  useEffect(() => {
    if (isConnected && address) {
      setSentryUser(address, chainId)
      addBreadcrumb('Wallet Connected', 'user', { address, chainId })
    } else {
      clearSentryUser()
    }
  }, [isConnected, address, chainId])
  
  // Show warning toast when on wrong network
  useEffect(() => {
    if (isWrongNetwork && chain) {
      toast.error(
        `Wrong network detected! Please switch to Ethereum Mainnet. Currently on: ${chain.name}`,
        {
          duration: 5000,
          id: 'wrong-network-toast', // Prevent duplicate toasts
        }
      )
    }
  }, [isWrongNetwork, chain])

  // Header configuration
  const menuItems: HeaderMenuItem[] = [
    { label: 'Home', href: '/', active: location.pathname === '/', onClick: () => navigate('/') },
    { label: 'Dashboard', href: '/dashboard', active: location.pathname === '/dashboard', onClick: () => navigate('/dashboard') },
    { label: 'Vaults', href: '/vaults', active: location.pathname === '/vaults', onClick: () => navigate('/vaults') },
    { label: 'LOCs', href: '/locs', active: location.pathname === '/locs', onClick: () => navigate('/locs') },
    { label: 'Loans', href: '/loans', active: location.pathname === '/loans', onClick: () => navigate('/loans') },
    { label: 'About', href: '/about', active: location.pathname === '/about', onClick: () => navigate('/about') },
  ]

  // Wallet button handler - disconnect if connected, connect if not
  const handleWalletAction = () => {
    if (isConnected) {
      // Open account modal to show disconnect option
      openAccountModal?.()
    } else {
      // Open connect modal
      openConnectModal?.()
    }
  }
  
  // Show network warning in button if on wrong network
  const actionLabel = isConnected && address 
    ? isWrongNetwork 
      ? '⚠️ Wrong Network'
      : formatAddress(address)
    : 'Connect Wallet'

  // Footer configuration
  const footerLinkGroups: WebsiteFooterLinkGroup[] = [
    {
      heading: 'Products',
      links: [
        { label: 'Vaults', href: '/vaults', onClick: () => navigate('/vaults') },
        { label: 'Letters of Credit', href: '/locs', onClick: () => navigate('/locs') },
        { label: 'Loans', href: '/loans', onClick: () => navigate('/loans') },
        { label: 'Dashboard', href: '/dashboard', onClick: () => navigate('/dashboard') },
      ]
    },
    {
      heading: 'Resources',
      links: [
        { label: 'Documentation', href: 'https://docs.anvilapplication.com', isExternal: true },
        { label: 'API Reference', href: 'https://docs.anvilapplication.com/api', isExternal: true },
        { label: 'Support', href: '/support', onClick: () => navigate('/support') },
        { label: 'FAQ', href: '/faq', onClick: () => navigate('/faq') },
      ]
    },
    {
      heading: 'Company',
      links: [
        { label: 'About', href: 'https://www.bitcoin.com/about-us/', isExternal: true },
        { label: 'Contact', href: 'https://www.bitcoin.com/contact-us/', isExternal: true },
        { label: 'Legal', href: 'https://www.bitcoin.com/legal/user-agreement/', isExternal: true },
        { label: 'Privacy', href: 'https://www.bitcoin.com/legal/privacy-policy/', isExternal: true },
      ]
    },
  ]

  return (
    <>
      <NetworkWarning />
      
      {/* Theme Toggle - Fixed position in top-right */}
      <div className="fixed top-4 right-4 z-50 hidden sm:block">
        <ThemeToggle />
      </div>
      
      {/* Mobile Theme Toggle - In header area */}
      <div className="fixed top-4 right-16 z-50 sm:hidden">
        <ThemeToggle />
      </div>
      
      <WebLayout
        headerProps={{
          menuItems,
          actionLabel,
          onActionClick: handleWalletAction
        }}
        footerProps={{
          linkGroups: footerLinkGroups,
          showDownloadSection: true
        }}
      >
        {children}
      </WebLayout>
    </>
  )
}
